import { useCallback, useState } from 'react';
import type { GraphQLError } from 'graphql';
import config from '@bymason/runtime-config';
import ApiError from './api-error';
import { getErrorCode, getErrorData, getErrorMessage } from './error-utils';
import { CallJsonEndpointOptions, callJsonEndpoint } from './call-json-endpoint';

export type { GraphQLError } from 'graphql';

export const GRAPHQL_API_ERRORS_FLAG = 'graphql-api-errors';

export type GqlDataHook<CriteriaType, ResultType, ErrorDataType> = [
  execute: (criteria: CriteriaType, signal?: AbortSignal | null) => void,
  result: ResultType,
  error: string | null,
  busy: boolean,
  errorCode: number,
  errorData: ErrorDataType | GraphQLError[] | undefined,
  clearData: () => void
];

export interface GqlEndpointCallSignature
  extends Omit<CallJsonEndpointOptions, 'body' | 'errorOn404' | 'headers' | 'method' | 'mode' | 'url'> {
  mutation?: string;
  query?: string;
  variables?: unknown;
}

export interface GqlResponse<TData = unknown> {
  data?: TData;
  errors?: GraphQLError[];
  extensions?: unknown;
  status: number;
  [key: string]: unknown;
}

export function callGqlEndpoint<T>({ mutation, query, variables, ...jsonOptions }: GqlEndpointCallSignature) {
  if (!mutation && !query) {
    throw new Error('No query or mutation was provided.');
  }

  const url = `${config.GqlDomainUrl}/graphql`;
  const body = { query: query ? `query ${query}` : `mutation ${mutation}`, variables };

  return callJsonEndpoint<GqlResponse<T>>({
    ...jsonOptions,
    body: JSON.stringify(body),
    method: 'POST',
    url,
  });
}

export function formatGqlParams(options: unknown): string {
  function formatValue(value: unknown): string {
    if (typeof value === 'string') {
      return `"${value}"`;
    } else if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        const formattedArray = value.map(formatValue).join(', ');
        return `[${formattedArray}]`;
      } else {
        const formattedObject = formatGqlParams(value);
        return `{ ${formattedObject} }`;
      }
    } else {
      return String(value);
    }
  }

  const normOpts = options as Record<string, unknown>;
  return Object.keys(normOpts)
    .filter((k) => typeof normOpts[k] !== 'undefined')
    .map((k) => `${k}: ${formatValue(normOpts[k])}`)
    .join(', ');
}

export function getGqlErrorMessage(err: unknown) {
  const errData = getErrorData<GraphQLError[]>(err);

  if (errData) {
    console.error('GraphQL Errors:', errData);
    return errData[0].message;
  }

  return getErrorMessage(err);
}

export function useGqlDataHook<O, T, E = void>(
  onGetGqlCallback: (options: O, signal?: AbortSignal | null) => Promise<void | GqlResponse<T>>,
  returnDataOnError = false,
  busyOnMount = false
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(busyOnMount); // this is jank solution, should be false in 99% of cases
  const [errorCode, setErrorCode] = useState(0);
  const [errorData, setErrorData] = useState<E | GraphQLError[]>();

  const getData = useCallback(
    async (options: O, signal?: AbortSignal | null) => {
      try {
        setBusy(true);
        setError(null);
        setErrorCode(0);

        const gqlResp = await onGetGqlCallback(options, signal);

        if (!gqlResp?.errors) {
          setData(gqlResp?.data || null);
        } else {
          setError(getGqlErrorMessage(gqlResp));
          returnDataOnError && setData(gqlResp?.data || null);
        }
      } catch (error) {
        const err = error as Error | ApiError<E>;
        if (err.name === 'AbortError') {
          setData(null);
        } else {
          setError(getGqlErrorMessage(err));
          setErrorCode(getErrorCode(err));
          setErrorData(getErrorData<E | GraphQLError[]>(err));
        }
      } finally {
        setBusy(false);
      }
    },
    [onGetGqlCallback, returnDataOnError]
  );

  const clearData = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return [getData, data, error, busy, errorCode, errorData, clearData] as GqlDataHook<O, void | T, E>;
}
